import { useState } from 'react';
import { Plus, Pencil, Trash2, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogSheetContent, DialogSheetHeader, DialogSheetBody, DialogDescription } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { formatAmount, getIcon, categoryColors, categoryIcons, catColor, catTint } from '../lib/constants';
import { createCategory, updateCategory, deleteCategory } from '../lib/api';
import { toast } from 'sonner';
import { DateRangePicker } from '../components/DateRangePicker';
import { useAmountsVisibility } from '../context/AmountsVisibilityContext';
import SwipeableRow, { isTouchDevice } from '../components/SwipeableRow';

export default function Categories({ categories, onRefresh, dateRange, setDateRange, budgetPeriod, onBack }) {
  const [showAdd, setShowAdd] = useState(false);
  const [editCat, setEditCat] = useState(null);
  const [form, setForm] = useState({ name: '', icon: 'MoreHorizontal', color: '#10b981', budget: '', type: 'expense' });
  const [saving, setSaving] = useState(false);
  // icon → name of the (other) category already using it.
  const usedIcons = new Map(
    categories.filter(c => c.id !== editCat?.id).map(c => [c.icon, c.name])
  );

  const openEdit = (cat) => {
    setEditCat(cat);
    setForm({ name: cat.name, icon: cat.icon, color: cat.color, budget: String(cat.budget || ''), type: cat.type });
  };

  // New categories start on the first free icon, not one that's taken.
  const resetForm = () => {
    const firstFree = categoryIcons.find(i => !categories.some(c => c.icon === i)) || 'MoreHorizontal';
    setForm({ name: '', icon: firstFree, color: '#10b981', budget: '', type: 'expense' });
    setEditCat(null);
  };

  // Same missing-busy-state bug as TransactionDialog.js's handleSave — no
  // visual difference between "working on it" and "did nothing," and no
  // guard against a second tap firing a duplicate create while the first
  // request is still in flight.
  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    const data = { ...form, budget: parseFloat(form.budget) || 0 };
    try {
      if (editCat) {
        await updateCategory(editCat.id, data);
        toast.success('Categoria actualizada');
      } else {
        await createCategory(data);
        toast.success('Categoria creada');
      }
      resetForm();
      setShowAdd(false);
      onRefresh();
    } catch (e) {
      toast.error('Error al guardar categoria');
    } finally {
      setSaving(false);
    }
  };

  // Confirmed before deleting — the backend cascade-deletes every
  // transaction ever recorded under this category (not just the ones
  // visible in the current date-range filter), so this is a real,
  // permanent, potentially large-scale deletion, not just removing an
  // empty label. Genuinely warrants the confirm the apple-design skill
  // reserves for irreversible actions.
  const handleDelete = async (cat) => {
    if (!window.confirm(
      `¿Eliminar la categoría "${cat.name}"? Esto también elimina TODAS las transacciones registradas en esta categoría (no solo las de este período) — no se puede deshacer.`
    )) return;
    try {
      await deleteCategory(cat.id);
      toast.success('Categoria eliminada');
      onRefresh();
    } catch (e) {
      toast.error('Error al eliminar');
    }
  };

  const expenseCategories = categories.filter(c => c.type === 'expense' || c.type === 'both');
  const incomeCategories = categories.filter(c => c.type === 'income');

  return (
    <div className="space-y-6" data-testid="categories-page">
      <div className="sticky top-[env(safe-area-inset-top)] z-10 bg-surface pb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          {/* No nav item of its own anymore (opened from Transacciones /
              Dashboard), so it needs its own way back. */}
          {onBack && (
            <button
              type="button"
              data-testid="categories-back-btn"
              onClick={onBack}
              aria-label="Volver"
              className="hit-44 w-10 h-10 -ml-2 rounded-xl flex items-center justify-center text-slate-300 hover:text-white hover:bg-white/5 transition-colors active:scale-90 shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white">Categorías</h1>
            <p className="text-slate-400 text-sm mt-1">Organiza tus ingresos y gastos</p>
          </div>
        </div>
        {/* Always flex-row (not flex-col on mobile) — DateRangePicker and
            "Nueva" are both naturally compact pill controls that fit on one
            row together; stacking them full-width was wasting a whole extra
            row of vertical space on small screens for no reason. */}
        <div className="flex flex-row flex-wrap items-center gap-3 w-full sm:w-auto">
          <DateRangePicker dateRange={dateRange} setDateRange={setDateRange} budgetPeriod={budgetPeriod} className="flex-1" />
          <Button
            data-testid="add-category-btn"
            onClick={() => { resetForm(); setShowAdd(true); }}
            className="bg-violet-500 hover:bg-violet-600 text-on-brand rounded-full px-5 shadow-[0_0_20px_rgba(139,92,246,0.25)] whitespace-nowrap"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Nueva
          </Button>
        </div>
      </div>

      {/* Expense Categories */}
      {expenseCategories.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">Gastos</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {expenseCategories.map((cat, i) => (
              <CategoryCard key={cat.id} cat={cat} index={i} onEdit={() => openEdit(cat)} onDelete={() => handleDelete(cat)} />
            ))}
          </div>
        </div>
      )}

      {/* Income Categories */}
      {incomeCategories.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">Ingresos</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {incomeCategories.map((cat, i) => (
              <CategoryCard key={cat.id} cat={cat} index={i} onEdit={() => openEdit(cat)} onDelete={() => handleDelete(cat)} />
            ))}
          </div>
        </div>
      )}

      {categories.length === 0 && (
        <div className="text-center py-16">
          <p className="text-slate-400 mb-4">No tienes categorias aun</p>
          <Button
            data-testid="add-first-category-btn"
            onClick={() => { resetForm(); setShowAdd(true); }}
            className="bg-violet-500 hover:bg-violet-600 text-on-brand rounded-full px-6"
          >
            Crear primera categoria
          </Button>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showAdd || !!editCat} onOpenChange={(open) => { if (!open) { setShowAdd(false); setEditCat(null); resetForm(); } }}>
        <DialogSheetContent data-testid="category-dialog">
          <DialogDescription className="sr-only">
            {editCat ? 'Modifica los datos de la categoria' : 'Crea una categoria para organizar tus transacciones'}
          </DialogDescription>

          <DialogSheetHeader
            title={editCat ? 'Editar categoría' : 'Nueva categoría'}
            onCancel={() => { setShowAdd(false); setEditCat(null); resetForm(); }}
            cancelTestId="category-cancel-btn"
            action={
              <button
                type="button"
                data-testid="save-category-btn"
                onClick={handleSave}
                disabled={!form.name.trim() || saving}
                className="text-[15px] font-semibold text-violet-400 px-2 py-2 -mr-2 rounded-lg transition-colors active:scale-[0.97] disabled:opacity-40"
              >
                {saving ? (
                  <div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                ) : (editCat ? 'Guardar' : 'Crear')}
              </button>
            }
          />

          <DialogSheetBody>
            <div className="space-y-2">
              <Label className="text-slate-300">Nombre</Label>
              <Input
                data-testid="category-name-input"
                value={form.name}
                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                placeholder="Ej: Comida"
                className="bg-slate-950/50 border-slate-700 h-11 rounded-xl text-white placeholder:text-slate-400"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Tipo</Label>
              <Select value={form.type} onValueChange={v => setForm(p => ({ ...p, type: v }))}>
                <SelectTrigger className="bg-slate-950/50 border-slate-700 h-11 rounded-xl text-white" data-testid="category-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="expense">Gasto</SelectItem>
                  <SelectItem value="income">Ingreso</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Presupuesto ($)</Label>
              <Input
                data-testid="category-budget-input"
                type="number"
                value={form.budget}
                onChange={e => setForm(p => ({ ...p, budget: e.target.value }))}
                placeholder="Ej: 300"
                className="bg-slate-950/50 border-slate-700 h-11 rounded-xl text-white placeholder:text-slate-400"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Icono</Label>
              {/* One icon per category: icons another category already uses
                  are shown but disabled. Scrolls inside the dialog. */}
              <div className="grid grid-cols-6 gap-2 max-h-48 overflow-y-auto pr-1" data-testid="icon-picker">
                {categoryIcons.map(iconName => {
                  const Icon = getIcon(iconName);
                  const takenBy = usedIcons.get(iconName);
                  return (
                    <button
                      key={iconName}
                      type="button"
                      data-testid={`icon-${iconName}`}
                      disabled={!!takenBy}
                      title={takenBy ? `En uso: ${takenBy}` : undefined}
                      aria-label={takenBy ? `${iconName} (en uso: ${takenBy})` : iconName}
                      onClick={() => setForm(p => ({ ...p, icon: iconName }))}
                      className={`w-11 h-11 rounded-lg flex items-center justify-center transition-all duration-200 disabled:opacity-25 disabled:cursor-not-allowed ${
                        form.icon === iconName
                          ? 'bg-violet-500/20 text-violet-400 ring-1 ring-violet-500/50'
                          : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Color</Label>
              <div className="flex gap-3 flex-wrap">
                {categoryColors.map(c => (
                  <button
                    key={c}
                    type="button"
                    data-testid={`color-${c}`}
                    onClick={() => setForm(p => ({ ...p, color: c }))}
                    className={`hit-44 w-10 h-10 rounded-full transition-transform duration-200 hover:scale-110 ${form.color === c ? 'ring-2 ring-white ring-offset-2 ring-offset-slate-900 scale-110' : ''}`}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>
          </DialogSheetBody>
        </DialogSheetContent>
      </Dialog>
    </div>
  );
}

function CategoryCard({ cat, index, onEdit, onDelete }) {
  const { hidden } = useAmountsVisibility();
  const Icon = getIcon(cat.icon);
  const pct = cat.budget > 0 ? Math.min((cat.spent / cat.budget) * 100, 100) : 0;
  const isExpense = cat.type === 'expense' || cat.type === 'both';
  const remaining = cat.budget - cat.spent;

  // Compact tile — matches the density of the category picker grid in the
  // transaction modal (icon-forward, tight spacing). The "Gasto"/"Ingreso"
  // type badge and the "Gastado" caption are dropped from here: every card
  // in this grid already sits under a "Gastos"/"Ingresos" section header,
  // so repeating the type on each one was pure redundant text; the
  // amount + progress bar already make "spent vs budget" obvious without
  // a label spelling it out. Both still show up in the peek detail below.
  const card = (
    <div
      className={`glass-card rounded-2xl p-4 relative overflow-hidden noise-overlay transition-colors duration-150 ${isTouchDevice ? '' : 'hover:bg-white/[0.02]'} animate-fade-in-up stagger-${index + 1}`}
      style={{ opacity: 0 }}
      data-testid={`category-card-${cat.id}`}
    >
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: catTint(cat.color) }}>
            <Icon className="w-4 h-4" style={{ color: catColor(cat.color) }} />
          </div>
          <h3 className="text-sm font-semibold text-white truncate">{cat.name}</h3>
        </div>
        {/* Touch devices get long-press → Editar/Eliminar instead (see the
            SwipeableRow wrapper below) — always-visible buttons here would
            just be redundant clutter on a tile this small. */}
        {!isTouchDevice && (
          <div className="flex items-center gap-0.5 shrink-0">
            <button
              data-testid={`edit-category-${cat.id}`}
              onClick={onEdit}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-white hover:bg-white/10 transition-colors duration-200"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button
              data-testid={`delete-category-${cat.id}`}
              onClick={onDelete}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-rose-400 hover:bg-rose-400/10 transition-colors duration-200"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>

      {isExpense && cat.budget > 0 && (
        <div>
          <div className="flex items-baseline justify-between text-xs mb-1.5">
            <span className="text-slate-300 font-medium metric-value">{formatAmount(cat.spent, hidden)}</span>
            <span className="text-slate-500 metric-value">/ {formatAmount(cat.budget, hidden)}</span>
          </div>
          <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-slate-800">
            <div
              className="h-full rounded-full budget-progress-bar"
              style={{
                transform: `scaleX(${pct / 100})`,
                backgroundColor: pct > 90 ? 'var(--danger)' : pct > 70 ? 'var(--warning)' : catColor(cat.color)
              }}
            />
          </div>
          <p className="text-[11px] text-slate-500 mt-1 truncate">
            {remaining >= 0 ? `Quedan ${formatAmount(remaining, hidden)}` : `Te pasaste ${formatAmount(Math.abs(remaining), hidden)}`}
          </p>
        </div>
      )}

      {cat.type === 'income' && (
        <p className="text-sm font-semibold text-emerald-400 metric-value">{formatAmount(cat.earned, hidden)}</p>
      )}
    </div>
  );

  return (
    <SwipeableRow
      testId={`swipe-category-${cat.id}`}
      disabled={!isTouchDevice}
      swipeDisabled
      leftAction={{ icon: Pencil, bg: 'bg-violet-500', label: 'Editar', onCommit: onEdit }}
      rightAction={{ icon: Trash2, bg: 'bg-rose-500', label: 'Eliminar', onCommit: onDelete }}
      peekContent={
        <div className="text-center">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: catTint(cat.color) }}>
            <Icon className="w-6 h-6" style={{ color: catColor(cat.color) }} />
          </div>
          <p className="text-lg font-semibold text-white">{cat.name}</p>
          <p className="text-sm text-slate-400 mt-1">{cat.type === 'income' ? 'Ingreso' : 'Gasto'}</p>
          {isExpense && cat.budget > 0 ? (
            <div className="mt-4 text-left">
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Gastado</span>
                <span className="text-white font-medium metric-value">{formatAmount(cat.spent, hidden)} / {formatAmount(cat.budget, hidden)}</span>
              </div>
              <p className={`text-xs mt-1 ${remaining < 0 ? 'text-rose-400' : 'text-slate-500'}`}>
                {remaining >= 0 ? `Quedan ${formatAmount(remaining, hidden)}` : `Te pasaste ${formatAmount(Math.abs(remaining), hidden)}`}
              </p>
            </div>
          ) : cat.type === 'income' ? (
            <p className="text-2xl font-bold text-emerald-400 mt-3 metric-value">{formatAmount(cat.earned, hidden)}</p>
          ) : null}
        </div>
      }
    >
      {card}
    </SwipeableRow>
  );
}
